/**
 * The saved settings and the login: localStorage in and out, the read-only
 * views the rest of the client imports, and the one-second upload of changed
 * settings to the account. Pushing loaded settings into game state is
 * Games.applySettingsToGame, so this module stays clear of the UI and input
 * modules.
 */
import { loadJsonObject, saveJson, type StorageKey } from '../services/LocalStorage';
import type { Settings } from './Config';
import { DEVELOPMENT } from './Constants';
import { ajaxPost } from './Http';
import type { AuthConfig } from './types/Auth';

/**
 * The saved settings, as loaded and last changed here. Readers import this
 * view; a change goes through setSettings, which saves it, or
 * setSettingsUnsaved, which the next save carries along.
 */
const settingsState: Settings = {};
export const settings: Readonly<Settings> = settingsState;

/**
 * The saved login, empty when signed out. loadAuth and setAuth replace it
 * whole, and importers read the current one through this live binding.
 */
export let auth: Readonly<AuthConfig> = {};

/** Last remote settings JSON sent to the server, to skip the upload when nothing changed. */
let lastRemoteSettingsJson = '';

/**
 * What the account stores: every setting but the id, which names this
 * install, and the display group, which follows this device's GPU.
 */
export type RemoteSettings = Omit<Settings, 'id' | 'display'>;

/**
 * Drops the settings that never leave this device. Applied to the upload and
 * to what the account sends back, so neither side's copy can overwrite them.
 */
export function toRemoteSettings(settings: Settings): RemoteSettings {
  const { id: _id, display: _display, ...remoteSettings } = settings;
  return remoteSettings;
}

/**
 * A stored object as the given shape. These keys are only ever written from
 * here, so a plain object is as far as the check goes. A hand-edited string
 * or null used to spread its characters into the settings, or take the client
 * down in the auth check.
 */
function loadStoredObject<T extends object>(key: StorageKey): T {
  return (loadJsonObject(key) ?? {}) as T;
}

/** Reads the saved settings from localStorage. */
export function loadSettings() {
  const stored = loadStoredObject<Settings>('settings');

  // Into the live object, still empty this early in startup
  Object.assign(settingsState, stored);

  // Log settings in development for debugging
  if (DEVELOPMENT) {
    console.log(stored);
  }
}

/** Sets the given settings and saves them all to localStorage. */
export function setSettings(changes: Settings) {
  // Each setting passed replaces the stored one whole, as legacy's did; to
  // change one field of a group, spread the rest of it in:
  // setSettings({ game: { ...settings.game, X: value } }). A deep merge kept
  // whatever the caller had dropped, so a keybind put back to its default
  // kept its old saved keys and got them back on the next visit.
  Object.assign(settingsState, changes);

  // Persist all settings to localStorage
  saveJson('settings', settingsState);
}

/**
 * Sets the given settings in memory without saving: a live preview that a
 * later setSettings commits, or a default the game assumes until the player
 * chooses. The next save of any setting carries them along, as it carried
 * legacy's direct writes.
 */
export function setSettingsUnsaved(changes: Settings) {
  Object.assign(settingsState, changes);
}

/** Deletes a single setting key and persists to localStorage. */
export function removeSetting(key: keyof Settings) {
  // Delete the setting if it exists
  if (settingsState[key] != null) {
    delete settingsState[key];
  }

  // Persist updated settings to localStorage
  saveJson('settings', settingsState);
}

/** True when the login gave every field the client needs. A null field counts as missing, as the type says. */
function isCompleteAuth(candidate: AuthConfig): boolean {
  return (
    candidate.tokens?.settings != null &&
    candidate.tokens.game != null &&
    candidate.identityprovider != null &&
    candidate.loginname != null
  );
}

/** Loads the login from localStorage, clearing it unless complete, and returns whether the user is logged in. */
export function loadAuth() {
  const stored = loadStoredObject<AuthConfig>('auth');
  if (isCompleteAuth(stored)) {
    auth = stored;
    return true;
  }

  // An incomplete login is cleared from storage too, as legacy did
  setAuth({});
  return false;
}

/** Sets the login and saves it to localStorage, as empty unless it is complete. */
export function setAuth(next: AuthConfig) {
  auth = isCompleteAuth(next) ? next : {};
  saveJson('auth', auth);
}

/** Whether the once-a-second upload is running. */
let remoteSettingsSyncStarted = false;

/**
 * Starts the once-a-second upload of changed settings to the account. Login
 * calls this once the account's copy has been read, so a device whose fetch
 * failed never overwrites the account with its own copy; legacy polled from
 * startup on any token.
 */
export function startRemoteSettingsSync() {
  if (remoteSettingsSyncStarted) {
    return;
  }
  remoteSettingsSyncStarted = true;
  setInterval(syncRemoteSettings, 1000);
}

/** Uploads changed settings to the remote service (fire-and-forget, auth required). */
async function syncRemoteSettings() {
  // A dev build is usually logged in through VITE_DEV_AUTH, and nothing it
  // does to settings should reach the real account behind those tokens. Plain
  // localhost isn't dev mode, so the sync can still be exercised there.
  if (DEVELOPMENT) {
    return;
  }

  // Only sync if user is authenticated with settings token
  const settingsToken = auth.tokens?.settings;
  if (!settingsToken) {
    return;
  }

  const remoteSettingsJson = JSON.stringify(toRemoteSettings(settingsState));

  // Skip if settings haven't changed since last sync
  if (remoteSettingsJson === lastRemoteSettingsJson) {
    return;
  }

  // Update last sent JSON before attempting upload
  lastRemoteSettingsJson = remoteSettingsJson;

  try {
    // The JSON text is the raw body, under the form content type, exactly as
    // legacy's jQuery call sent it; the backend has only ever seen that shape.
    const result = await ajaxPost<{ result?: number }>('/settings', remoteSettingsJson, settingsToken);

    // Reset if server didn't acknowledge success
    if (result.result !== 1) {
      lastRemoteSettingsJson = '';
    }
  } catch (error) {
    // Reset on error so we retry next time
    lastRemoteSettingsJson = '';

    // Silent fail - this is fire-and-forget
    if (DEVELOPMENT) {
      console.error('Failed to sync remote settings:', error);
    }
  }
}
